import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Heart, 
  Search, 
  Plus, 
  Trash2, 
  Mail, 
  Phone, 
  MessageSquare,
  Users,
  Shield,
  CheckCircle
} from "lucide-react";
import { Consultant } from "./ConsultantsTab";

export interface Partner {
  id: string;
  name: string;
  company?: string;
  type: 'internal' | 'external';
  role: string;
  email?: string;
  phone?: string;
  dealIds?: string[];
  addedAt?: any;
  isVerified?: boolean;
}

interface PartnersTabProps {
  partners: Partner[];
  consultants?: Consultant[];
  favoriteConsultants?: string[];
  onAddClick?: () => void;
  onRemove?: (id: string) => void;
  onToggleFavorite?: (id: string) => void;
  onViewConsultants?: () => void;
  onContact?: (id: string) => void;
  language: 'en' | 'fr';
  formatCurrency?: (amount: number, currency?: string) => string;
  showAddButton?: boolean;
}

const translations = {
  en: {
    myPartners: "My Partners",
    partnersDesc: "Internal and external business partners. Add consultants to favorites via ❤️, and send a contact request to schedule a meeting.",
    addPartner: "Add Partner",
    noPartners: "No partners added yet",
    addFirstPartner: "Add Your First Partner",
    role: "Role",
    contact: "Contact",
    verified: "Verified",
    searchPlaceholder: "Search (name, specialty, service)...",
    allTypes: "All types",
    favoriteConsultants: "Favorite Consultants",
    internalPartners: "Internal Partners",
    externalPartners: "External Partners",
    noFavoriteConsultants: "No favorite consultants",
    addFavoriteFromConsultants: "Add consultants to favorites from the \"Consultants\" tab",
    viewConsultants: "View Consultants",
    partnersFound: "partner(s) found",
    favoriteConsultantsCount: "favorite(s)"
  },
  fr: {
    myPartners: "Mes Partenaires",
    partnersDesc: "Partenaires internes et externes. Ajoutez des consultants via ❤️, et envoyez une demande de contact pour planifier une réunion.",
    addPartner: "Ajouter un Partenaire",
    noPartners: "Aucun partenaire ajouté",
    addFirstPartner: "Ajouter votre premier Partenaire",
    role: "Rôle",
    contact: "Contacter",
    verified: "Vérifié",
    searchPlaceholder: "Rechercher (nom, spécialité, service)...",
    allTypes: "Tous les types",
    favoriteConsultants: "Consultants Favoris",
    internalPartners: "Partenaires Internes",
    externalPartners: "Partenaires Externes",
    noFavoriteConsultants: "Aucun consultant favori",
    addFavoriteFromConsultants: "Ajoutez des consultants aux favoris depuis l'onglet \"Consultants\"",
    viewConsultants: "Voir les Consultants",
    partnersFound: "partenaire(s) trouvé(s)",
    favoriteConsultantsCount: "favori(s)"
  }
};

export function PartnersTab({
  partners,
  consultants = [],
  favoriteConsultants = [],
  onAddClick,
  onRemove,
  onToggleFavorite,
  onViewConsultants,
  onContact,
  language,
  formatCurrency,
  showAddButton = true
}: PartnersTabProps) {
  const t = translations[language];
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "favorites" | "internal" | "external">("all");

  const favoriteConsultantsList = useMemo(() => 
    consultants.filter(c => favoriteConsultants.includes(c.id)),
    [consultants, favoriteConsultants]
  );

  const filteredPartners = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return partners.filter((p) => {
      const matchesSearch = !s ||
        [p.name, p.role, p.company]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(s));
      
      const matchesFilter = typeFilter === "all" || typeFilter === "favorites" || p.type === typeFilter;
      
      return matchesSearch && matchesFilter;
    });
  }, [partners, searchTerm, typeFilter]);

  const handleContact = (id: string) => {
    if (onContact) {
      onContact(id);
    } else {
      alert(language === 'fr' ? "Demande de contact envoyée ✅" : "Contact request sent ✅");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{t.myPartners}</CardTitle>
            <CardDescription className="max-w-xl">{t.partnersDesc}</CardDescription>
          </div>
          {showAddButton && onAddClick && (
            <Button onClick={onAddClick}>
              <Plus className="h-4 w-4 mr-1" />
              {t.addPartner}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            className="border rounded-md px-3 py-2 bg-background"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
          >
            <option value="all">{t.allTypes}</option>
            <option value="favorites">{t.favoriteConsultants}</option>
            <option value="internal">{t.internalPartners}</option>
            <option value="external">{t.externalPartners}</option>
          </select>
          <div className="text-sm text-muted-foreground flex items-center">
            ❤️ {favoriteConsultantsList.length} {t.favoriteConsultantsCount}
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-muted-foreground">
          {typeFilter === 'favorites' 
            ? `${favoriteConsultantsList.length} ${t.favoriteConsultantsCount}`
            : `${filteredPartners.length} ${t.partnersFound}`
          }
        </div>

        {/* Favorite Consultants Section */}
        {typeFilter === 'favorites' && (
          favoriteConsultantsList.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t.noFavoriteConsultants}</p>
              <p className="text-sm text-muted-foreground mb-4">{t.addFavoriteFromConsultants}</p>
              {onViewConsultants && (
                <Button variant="outline" onClick={onViewConsultants}>
                  {t.viewConsultants}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {favoriteConsultantsList.map(consultant => (
                <Card key={consultant.id} className="hover:shadow-lg transition-shadow border-red-200 bg-gradient-to-br from-background to-red-50/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-red-100 text-red-600">
                          {consultant.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{consultant.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{consultant.specialty}</p>
                      </div>
                      <Heart className="h-5 w-5 text-red-500 fill-red-500 flex-shrink-0" />
                    </div>
                    
                    {consultant.bio && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{consultant.bio}</p>
                    )}

                    <div className="flex gap-2 pt-2 border-t">
                      <Button 
                        size="sm" 
                        variant="default"
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleContact(consultant.id)}
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        {t.contact}
                      </Button>
                      {onToggleFavorite && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-muted-foreground hover:text-red-500"
                          onClick={() => onToggleFavorite(consultant.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}

        {/* Partners Section */}
        {typeFilter !== 'favorites' && (
          filteredPartners.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t.noPartners}</p>
              {showAddButton && onAddClick && (
                <Button onClick={onAddClick}>{t.addFirstPartner}</Button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Internal Partners */}
              {(typeFilter === 'all' || typeFilter === 'internal') && 
               filteredPartners.filter(p => p.type === 'internal').length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">{t.internalPartners}</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPartners.filter(p => p.type === 'internal').map(partner => (
                      <PartnerCard 
                        key={partner.id} 
                        partner={partner} 
                        onRemove={onRemove}
                        onContact={handleContact}
                        language={language}
                        t={t}
                        borderClass="border-blue-200"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* External Partners */}
              {(typeFilter === 'all' || typeFilter === 'external') && 
               filteredPartners.filter(p => p.type === 'external').length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">{t.externalPartners}</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPartners.filter(p => p.type === 'external').map(partner => (
                      <PartnerCard 
                        key={partner.id} 
                        partner={partner} 
                        onRemove={onRemove}
                        onContact={handleContact}
                        language={language}
                        t={t}
                        borderClass=""
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
}

function PartnerCard({ 
  partner, 
  onRemove,
  onContact,
  language, 
  t,
  borderClass 
}: { 
  partner: Partner; 
  onRemove?: (id: string) => void;
  onContact: (id: string) => void;
  language: 'en' | 'fr';
  t: typeof translations.en;
  borderClass: string;
}) {
  return (
    <Card className={`hover:shadow-lg transition-shadow ${borderClass} bg-gradient-to-br from-background to-blue-50/30`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-secondary/50">
              {partner.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{partner.name}</p>
            {partner.company && (
              <p className="text-sm text-muted-foreground truncate">{partner.company}</p>
            )}
          </div>
          {partner.isVerified && (
            <Badge variant="default" className="gap-1 flex-shrink-0">
              <CheckCircle className="h-3 w-3" />
              {t.verified}
            </Badge>
          )}
        </div>

        <div>
          <p className="text-sm text-muted-foreground">{t.role}:</p>
          <p className="text-sm font-medium">{partner.role}</p>
        </div>
        
        <div className="space-y-1 text-sm">
          {partner.email && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{partner.email}</span>
            </div>
          )}
          {partner.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span>{partner.phone}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <Button 
            size="sm" 
            variant="default"
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={() => onContact(partner.id)}
          >
            <MessageSquare className="h-4 w-4 mr-1" />
            {t.contact}
          </Button>
          {onRemove && (
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-destructive"
              onClick={() => onRemove(partner.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
